import { captureException } from '@sentry/nextjs'
import { createClient } from '../../prismicio'
import { Constants, Utils } from 'common/utils'
import {
  getHubSpotCTAs,
  getPosts,
  getTags,
  resolveBlogPostCTAs,
  transformBlogPost,
} from 'common/cms-sdk'
import {
  TBlogPost,
  TBlogPostResult,
  TBlogPostSummary,
  THubSpotCTA,
  TPlanSummary,
} from 'common/cms-types'
import { I18NConfig } from 'next/dist/server/config-shared'
import { NextPageWithLayout } from '../../types/nextPageWithLayout'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import SeoContainer from 'components/util/containers/SeoContainer'
import { slimDocument } from 'common/cms-helpers'
import { RichText, TableOfContents } from 'components/forms/RichText'
import { ArticleJsonLd, NextSeoProps } from 'next-seo'
import RelatedBlogPost from 'components/blog/RelatedBlogPost'
import PrismicContainer from 'components/util/containers/PrismicContainer'
import { seoStrings } from 'common/strings/seo-strings'
import FAQList from 'components/util/FAQList'
import { ItemListElements } from 'next-seo/lib/types'
import { blogBreadcrumb } from 'components/util/containers/Breadcrumbs'
import Strings from 'project/localisation'
import PrimeLondonBusinessAddressCard from 'components/cards/PrimeLondonBusinessAddressCard'
import AddressSection from 'components/sections/AddressSection'
import { blogStrings } from 'common/strings/blog-strings'
import Button from 'components/forms/Button'
import useWindowSize from 'components/util/useWindowSize'
import classNames from 'classnames'
import BlogSearch from 'components/blog/BlogSearch'
import { Query, RichTextField } from '@prismicio/types'
import useThrottle from 'common/useThrottle'
import standardiseTitle from 'project/standardiseTitle'
import usePreview from 'project/usePreview'
import BlogPostProductCTA from 'components/BlogPostProductCTA'
import { renderToString } from 'preact-render-to-string'
import ContactUsCard from 'components/cards/new/ContactUsCard'
import LondonVOPlusHeroCard from 'components/cards/new/LondonVOPlusHeroCard'
import { PlanSlug } from 'common/planSlugs'
import { getPlans } from 'common/planData'
import { TableSlice } from 'components/util/Table'
import SocialLinks from 'components/SocialLinks'

const PAGE_SIZE = 16
const RELATED_ARTICLE_PAGE_SIZE = 6
export type BlogPostType = {
  // @ts-ignore
  tags?: string[]
  tag?: string
  post: TBlogPost | null
  relatedArticles: TBlogPostSummary[]
  plans?: TPlanSummary[]
  ctas: THubSpotCTA[]
}
const BlogPostPage: NextPageWithLayout<BlogPostType> = ({
  ctas,
  plans,
  post,
  relatedArticles,
  tag,
  tags,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [posts, setPosts] = useState<Query<TBlogPostResult>>()
  const [pageNumber, setPageNumber] = useState(1)
  const [isTocOpen, setIsTocOpen] = useState(true)
  const router = useRouter()
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  const { preview } = usePreview()
  useEffect(() => {
    fetch(false)
  }, [router, query])

  const searchItems = useThrottle(() => {
    if (query !== search) {
      setQuery(search)
    }
  }, 200)

  useEffect(() => {
    searchItems(search)
  }, [search])

  const fetch = (next: boolean) => {
    setIsLoading(true)
    getPosts(
      createClient(Utils.getClientContext(router)),
      next ? pageNumber + 1 : pageNumber,
      Utils.getLocaleID(`${router.locale}`),
      PAGE_SIZE,
      next ? posts : undefined,
      tag,
      query,
    )
      .then((res) => {
        if (next) {
          setPageNumber(pageNumber + 1)
        } else {
          setPageNumber(1)
        }
        setPosts(res)
        setIsLoading(false)
      })
      .catch(() => {
        setIsLoading(false)
      })
  }

  if (!post) {
    return null
  }
  const { data } = post

  const title = standardiseTitle(`${data.meta_data_title} | Hoxton Mix`)
  const seoProps: NextSeoProps = {
    description: data.body_excerpt,
    openGraph: {
      article: {
        authors: ['https://www.facebook.com/hoxtonmix/'],
        modifiedTime: data.modified_date_iso,
        publishedTime: data.published_date_iso,
      },
      images: [Utils.prismicImageToOpenGraph(data.main_image)].filter((v) => !!v),
      title,
      type: 'article',
      url: `https://www.hoxtonmix.com/blog/${data.uid}/`,
    },
    title,
    twitter: {
      cardType: 'summary_large_image',
    },
  }
  const breadcrumb: ItemListElements[] = blogBreadcrumb().concat([
    {
      item: `https://www.hoxtonmix.com/blog/${data.uid}/`,
      name: `${data.meta_data_title}`,
      position: 3,
    },
  ])
  const windowSize = useWindowSize()

  useEffect(() => {
    setIsTocOpen(!windowSize.isMdOrLower)
  }, [windowSize.isMdOrLower])

  // Combine body content with all content_area slices for Table of Contents
  const allContent = [
    ...data.body,
    ...data.slices
      .filter((slice) => slice.slice_type === 'content_area')
      .flatMap((slice) =>
        slice.slice_type === 'content_area' ? slice.primary.body : [],
      ),
  ] as RichTextField

  const contents = (
    <TableOfContents isBlog text={allContent} shareTitle={data.title} />
  )
  const stringContents = renderToString(contents)
  const hideContents = stringContents?.length === 0

  return (
    <PrismicContainer>
      <SeoContainer breadcrumb={breadcrumb} seoProps={seoProps}>
        <main
          className={classNames({
            'position-relative': !windowSize.isXlOrHigher,
          })}
        >
          <BlogSearch
            key={post.id}
            posts={search ? posts : null}
            tags={tags}
            tag={tag}
            search={search}
            setSearch={setSearch}
            onShowMoreClick={() => fetch(false)}
            isLoading={isLoading}
          />
          {!search && (
            <>
              <div className='container px-xxl-52 px-xl-28 px-5 mt-14 mb-md-34 mb-8'>
                <div className={'px-md-20'}>
                  {!!data.published_date && (
                    <p
                      className={
                        'text-small-m text-small-m--mobile-small text-muted mb-md-10 mb-6'
                      }
                    >
                      {data.published_date}
                    </p>
                  )}
                  <h1
                    className={
                      'h3 h3--large text-bold mb-md-4 mb-2'
                    }
                  >
                    {data.title}{' '}
                    {!!preview && (
                      <div className='py-2'>
                        <Button
                          onClick={() =>
                            window.open(
                              `https://hoxton-mix.prismic.io/builder/pages/${post.id}`,
                            )
                          }
                        >
                          Edit
                        </Button>
                      </div>
                    )}
                  </h1>
                  <div className='blog-share'>
                    <span className='blog-share__label'>Share</span>
                    <SocialLinks
                      linkType='share'
                      urlToShare={`https://www.hoxtonmix.com/blog/${data.uid}/`}
                      shareTitle={data.title}
                    />
                  </div>
                </div>
                <div className={'rich-text-blog'}>
                  {!hideContents && (
                    <nav
                      className={classNames('blog-toc px-md-20 mb-18', {
                        'blog-toc--collapsed': !isTocOpen,
                      })}
                    >
                      <div className='blog-toc__header'>
                        <h4 className='blog-toc__title mb-0'>Table of Contents</h4>
                        <button
                          type='button'
                          className='blog-toc__toggle'
                          aria-expanded={isTocOpen}
                          aria-controls='blog-toc-list'
                          onClick={() => setIsTocOpen((value) => !value)}
                        >
                          {isTocOpen
                            ? windowSize.isMdOrLower
                              ? 'Collapse'
                              : 'Hide'
                            : windowSize.isMdOrLower
                              ? 'Expand'
                              : 'Show'}
                        </button>
                      </div>
                      {isTocOpen && (
                        <div id='blog-toc-list' className='blog-toc__list'>
                          {contents}
                        </div>
                      )}
                    </nav>
                  )}
                  <RichText isBlog text={data.body} shareTitle={data.title} />
                  {data.slices.map((slice, index) => {
                    if (slice.slice_type === 'table') {
                      return (
                        <TableSlice
                          key={index}
                          slice={slice}
                          className='text-center'
                        />
                      )
                    }
                    if (slice.slice_type === 'content_area') {
                      return (
                        <RichText
                          key={index}
                          isBlog
                          text={slice.primary.body}
                        />
                      )
                    }
                    return null
                  })}
                  <div className='mt-10'>
                    <BlogPostProductCTA ctas={ctas} />
                  </div>
                  <FAQList
                    styledAsRichTextForBlog={true}
                    slices={
                      data.slices.filter(
                        (slice) => slice.slice_type === 'faq',
                      ) as any
                    }
                  />
                </div>
              </div>
              {!!relatedArticles.length && (
                <div className={'blog-results bg-grey6 pt-lg-18 pt-6 pb-lg-14 pb-8'}>
                  <div className={'container px-xxl-27 px-lg-28 px-3-half'}>
                    <h2
                      className={classNames(
                        'text-center text-semibold mb-lg-18 mb-12',
                        windowSize.isSmOrLower ? 'text-xl' : 'h2',
                      )}
                    >
                      {blogStrings.handpickedRelatedArticles}
                    </h2>
                    <div className='row g-6'>
                      {relatedArticles.map((post) => (
                        <div className={'col-xl-4 col-md-6'}>
                          <RelatedBlogPost key={post.id} post={post} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          <div className='container mt-xl-24 mt-12 pb-10'>
            <div className='new-design pb-10'>
              <div className='row gy-5'>
                <div className='col-12 col-lg-6 '>
                  <ContactUsCard />
                </div>
                <div className='col-12 col-lg-6'>
                  <LondonVOPlusHeroCard
                    planSummary={
                      plans?.find(
                        (v: TPlanSummary) => v.slug === PlanSlug.SCAN_LITE,
                      )!
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <ArticleJsonLd
            type='Article'
            url={`https://www.hoxtonmix.com/blog/${data.uid}`}
            title={data.title}
            images={[data.main_image?.url]}
            publisherName={Constants.companyInformation.name}
            publisherLogo={seoStrings.appOGLogo}
            datePublished={data.published_date_iso}
            dateModified={data.modified_date_iso}
            authorName={'Chris Sees'}
            description={data.body_excerpt}
          />
        </main>
      </SeoContainer>
    </PrismicContainer>
  )
}

export async function getStaticPaths(c: I18NConfig) {
  const documents = await createClient().getAllByType('blog_post', {
    limit: process.env.BLOG_LIMIT
      ? parseInt(process.env.BLOG_LIMIT)
      : undefined,
    orderings: 'my.blog_post.published_date desc',
  })
  return {
    fallback: false,
    paths: documents
      .map((doc) => {
        return c.locales.map((locale: string) => ({
          locale,
          params: { uid: doc.uid },
        }))
      })
      .flat(),
  }
}
export async function getStaticProps(
  context: I18NConfig & { locale?: string; params: { uid: string } },
) {
  try {
    const client = createClient(context)
    const lang = Utils.getLocaleID(context.locale || context.defaultLocale)
    const plans = await getPlans()
    const posts = await getPosts(client, 1, lang, PAGE_SIZE, undefined)
    const tags = await getTags(client, lang)
    const allCtas = await getHubSpotCTAs(client, lang)
    let post: TBlogPost | null = null
    let relatedArticles: TBlogPostSummary[] = []
    let ctas: THubSpotCTA[] = allCtas
    try {
      const fullPost = (await client.getByUID('blog_post', context.params.uid, {
        lang,
      })) as unknown as TBlogPost
      if (fullPost.uid !== context.params.uid) {
        throw new Error(`${fullPost.uid} != ${context.params.uid}`)
      }
      post = await slimDocument(transformBlogPost(fullPost))
      if (!post) throw new Error()
      post.data.uid = context.params.uid
      ctas = resolveBlogPostCTAs(
        allCtas,
        fullPost.tags,
        (fullPost.data as any).hubspotcta?.id,
      )
      const relatedPosts = await getPosts(
        client,
        1,
        lang,
        RELATED_ARTICLE_PAGE_SIZE + 1,
        undefined,
        fullPost?.tags?.[0],
      )
      relatedArticles = relatedPosts.results
        .filter((v) => v.id !== post!.id)
        .slice(0, RELATED_ARTICLE_PAGE_SIZE)
        .map((article) =>
          slimDocument({
            ...article,
            data: {
              main_image: article.data.main_image,
              published_date: article.data.modified_date,
              title: article.data.title,
            },
          }),
        )
    } catch (e) {
      captureException(e, { tags: { uid: context.params.uid, url: `/blog/${context.params.uid}` } })
      throw e
    }

    return {
      props: {
        ctas,
        plans,
        post,
        posts,
        relatedArticles,
        tags,
      },
    }
  } catch (e) {
    captureException(e, { tags: { uid: context.params.uid, url: `/blog/${context.params.uid}` } })
    throw e
  }
}

export default BlogPostPage
